/**
 * Light/dark theme preference (client-only pivot).
 *
 * The resolved theme lives on <html class="dark"> — set before hydration by
 * the inline script from lib/theme-script.ts — and the explicit user choice
 * persists in localStorage. Persisting a UI preference is fine under the
 * zero-storage principle: it is not user content.
 */
"use client";

import { useSyncExternalStore } from "react";
import { THEME_COLORS, THEME_STORAGE_KEY } from "@/lib/theme-script";

export type Theme = "light" | "dark";

const listeners = new Set<() => void>();

function currentTheme(): Theme {
  if (typeof document === "undefined") return "light";
  return document.documentElement.classList.contains("dark") ? "dark" : "light";
}

/** Reactive resolved theme; SSR snapshot is "light". */
export function useTheme(): Theme {
  return useSyncExternalStore(
    (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    currentTheme,
    () => "light",
  );
}

export function setTheme(theme: Theme): void {
  document.documentElement.classList.toggle("dark", theme === "dark");
  document
    .querySelector('meta[name="theme-color"]')
    ?.setAttribute("content", THEME_COLORS[theme]);
  try {
    window.localStorage.setItem(THEME_STORAGE_KEY, theme);
  } catch {
    // Privacy modes may block localStorage — the toggle still works for
    // this tab via the <html> class.
  }
  listeners.forEach((listener) => listener());
}
